import { useEffect, useState } from 'react';
import { api, ErreurApi } from '../api.js';
import { tokens, couleursStatut, couleursGravite, couleursImpact, couleursUrgence, couleursType, champsDescription } from '../tokens.js';
import {
  Badge, BoiteErreur, labelChamp, inputBase,
  boutonPrincipal, boutonSecondaire, boutonDanger,
  formaterDateLongue,
} from '../helpers.jsx';
import {
  ArrowLeft, User, Bot, Shield, Paperclip, Send,
  CheckCircle, XCircle, MessageCircleQuestion, Copy, Eye, EyeOff,
} from 'lucide-react';

// Vue detail. Affiche les memes infos pour user et admin, mais l'admin
// dispose des actions (demander clarif, accepter, refuser, doublon, message).
export function TicketDetail({ token, utilisateur, onRetour }) {
  const [ticket, setTicket] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  // Etats des actions admin
  const [actionEnCours, setAction] = useState(null);
  const [contenuMessage, setContenuMessage] = useState('');
  const [messageInterne, setInterne] = useState(false);
  const [motifRefus, setMotifRefus] = useState('');
  const [doublonToken, setDoublon] = useState('');
  const [questionClarif, setQuestionClarif] = useState('');
  // Pour le user : reponse aux clarifications admin
  const [reponseClarif, setReponseClarif] = useState('');

  function recharger() {
    setChargement(true);
    api.get(`/tickets/${token}`)
      .then(d => setTicket(d.ticket))
      .catch(e => setErreur(e.message))
      .finally(() => setChargement(false));
  }

  useEffect(() => { recharger(); }, [token]);

  async function executer(action, body, params = {}) {
    setErreur('');
    setAction(action);
    try {
      const rep = await api.post(`/admin/tickets/${token}/${action}`, body);
      setTicket(rep.ticket);
      // Reset
      setContenuMessage(''); setInterne(false);
      setMotifRefus(''); setDoublon(''); setQuestionClarif('');
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    } finally {
      setAction(null);
    }
  }

  async function repondreClarif() {
    if (reponseClarif.trim().length < 3) return;
    setErreur('');
    try {
      // Le user repond a la derniere question demandee (recuperee dans les messages)
      const derniereQ = [...(ticket.messages || [])]
        .reverse()
        .find(m => m.type_message === 'clarif_demande');
      const question = derniereQ?.contenu || 'Reponse a la demande de clarification';
      await api.post(`/tickets/${token}/clarif`, {
        reponses: { [question]: reponseClarif.trim() },
      });
      setReponseClarif('');
      recharger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    }
  }

  if (chargement) return <Etat>Chargement…</Etat>;
  if (erreur || !ticket) return <Etat onRetour={onRetour}>{erreur || 'Ticket introuvable'}</Etat>;

  const statut  = couleursStatut[ticket.statut]    || couleursStatut.nouveau;
  const gravite = ticket.gravite_ia ? couleursGravite[ticket.gravite_ia] : null;
  const impact  = couleursImpact[ticket.impact];
  const urgence = couleursUrgence[ticket.urgence];

  const cheminService = ticket.parent_service_nom
    ? `${ticket.parent_service_nom} / ${ticket.service_nom}`
    : ticket.service_nom;

  const estAdmin = !!utilisateur.est_admin;
  const enAttenteUserClarif = ticket.statut === 'clarification_admin'
    && (ticket.declarant_id == utilisateur.id);

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <button onClick={onRetour} style={{
        display:'inline-flex', alignItems:'center', gap: 4,
        background:'transparent', border:'none', cursor:'pointer',
        color: tokens.inkSoft, fontSize: 13, marginBottom: 14, padding: 0,
      }}>
        <ArrowLeft size={14} /> Retour
      </button>

      {/* Entête */}
      <div style={cadre}>
        <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap', marginBottom: 12 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 13, color: tokens.inkMuted }}>
            #{ticket.token_public}
          </span>
          <Badge bg={statut.bg} fg={statut.fg}>{statut.label}</Badge>
          {ticket.type && couleursType[ticket.type] && (
            <Badge bg={couleursType[ticket.type].bg} fg={couleursType[ticket.type].fg}>
              {couleursType[ticket.type].label}
            </Badge>
          )}
          <Badge bg={impact.bg} fg={impact.fg}>{impact.label}</Badge>
          {ticket.urgence === 'urgent' && (
            <Badge bg={urgence.bg} fg={urgence.fg}>{urgence.label}</Badge>
          )}
          {gravite && <Badge bg={gravite.bg} fg={gravite.fg} taille="petit">IA: {gravite.label}</Badge>}
          <span style={{ marginLeft:'auto', fontSize: 11, color: tokens.inkMuted }}>
            {formaterDateLongue(ticket.cree_le)}
          </span>
        </div>

        <div style={{ fontSize: 12, color: tokens.inkSoft, marginBottom: 6 }}>
          <strong style={{ color: tokens.ink }}>Service :</strong> {cheminService}
        </div>
        <div style={{ fontSize: 12, color: tokens.inkSoft, marginBottom: ticket.envoi_force || ticket.source === 'sentry' ? 6 : 12 }}>
          <strong style={{ color: tokens.ink }}>Déclarant :</strong>{' '}
          {ticket.source === 'sentry'
            ? <Badge bg={tokens.warningBg} fg={tokens.warning} taille="petit">📊 Sentry (automatique)</Badge>
            : `${ticket.declarant_nom} (${ticket.declarant_login})`}
          {ticket.pris_par_nom && (
            <> · <strong style={{ color: tokens.ink }}>Pris en charge par :</strong> {ticket.pris_par_nom}</>
          )}
        </div>
        {ticket.source === 'sentry' && (
          <div style={{
            background: tokens.warningBg,
            border: `1px solid ${tokens.warning}30`,
            color: tokens.warning,
            padding: '10px 12px', borderRadius: 6, fontSize: 12,
            marginBottom: 12,
            display:'flex', flexWrap:'wrap', gap: 12, alignItems:'center',
          }}>
            <span><strong>{ticket.sentry_occurrences ?? 1}</strong> occurrence(s)</span>
            {ticket.sentry_niveau && <span>Niveau : <strong>{ticket.sentry_niveau}</strong></span>}
            {ticket.sentry_premiere_occurrence && (
              <span>1<sup>ère</sup> : {formaterDateLongue(ticket.sentry_premiere_occurrence)}</span>
            )}
            {ticket.sentry_derniere_occurrence && (
              <span>dernière : {formaterDateLongue(ticket.sentry_derniere_occurrence)}</span>
            )}
            {ticket.sentry_permalink && (
              <a href={ticket.sentry_permalink} target="_blank" rel="noreferrer"
                style={{ color: tokens.warning, fontWeight: 600, textDecoration:'underline', marginLeft:'auto' }}
              >
                Voir sur Sentry ↗
              </a>
            )}
          </div>
        )}
        {ticket.envoi_force && (
          <div style={{
            background: tokens.dangerBg, color: tokens.danger,
            padding: '8px 12px', borderRadius: 6, fontSize: 12,
            marginBottom: 12,
          }}>
            ⚠ Envoi forcé · Justification : {ticket.justification_envoi_force}
          </div>
        )}
        {ticket.motif_refus && (
          <div style={{
            background: tokens.dangerBg, color: tokens.danger,
            padding: '8px 12px', borderRadius: 6, fontSize: 12,
            marginBottom: 12,
          }}>
            <strong>Motif du refus :</strong> {ticket.motif_refus}
          </div>
        )}
        {ticket.doublon_de_token && (
          <div style={{
            background: tokens.surfaceAlt, color: tokens.inkSoft,
            padding: '8px 12px', borderRadius: 6, fontSize: 12,
            marginBottom: 12,
          }}>
            Doublon de #{ticket.doublon_de_token}
          </div>
        )}

        {ticket.resume_ia && (
          <div style={{
            padding: '10px 12px', background: tokens.accentBg, color: tokens.accent,
            borderRadius: 6, fontSize: 13, fontStyle: 'italic',
          }}>
            <strong>Résumé IA :</strong> {ticket.resume_ia}
          </div>
        )}
      </div>

      {/* Description en 3 champs (libellés selon type) */}
      <div style={{ ...cadre, marginTop: 12 }}>
        <Section titre={champsDescription[ticket.type || 'bug']?.action.label || 'Action'}>
          {ticket.description_action}
        </Section>
        <Section titre={champsDescription[ticket.type || 'bug']?.observation.label || 'Observation'}>
          {ticket.description_observation}
        </Section>
        <Section titre={champsDescription[ticket.type || 'bug']?.attendu.label || 'Attendu'} derniere>
          {ticket.description_attendu}
        </Section>
      </div>

      {/* Pieces jointes */}
      {ticket.pieces_jointes?.length > 0 && (
        <div style={{ ...cadre, marginTop: 12 }}>
          <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 600, color: tokens.inkSoft, textTransform:'uppercase', letterSpacing: 0.5 }}>
            Pièces jointes ({ticket.pieces_jointes.length})
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap: 10 }}>
            {ticket.pieces_jointes.map(pj => (
              <a key={pj.id} href={pj.url} target="_blank" rel="noreferrer"
                style={{
                  display:'flex', alignItems:'center', gap: 6,
                  background: tokens.surfaceAlt, padding: '6px 10px', borderRadius: 6,
                  fontSize: 12, color: tokens.inkSoft, textDecoration:'none',
                  border: `1px solid ${tokens.border}`,
                }}
              >
                <Paperclip size={12} /> {pj.nom_original}
                <span style={{ color: tokens.inkMuted }}>({Math.round(pj.taille_octets/1024)} Ko)</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Reponse user a clarification admin */}
      {enAttenteUserClarif && (
        <div style={{ ...cadre, marginTop: 12, borderColor: tokens.warning }}>
          <div style={{ fontSize: 13, color: tokens.warning, marginBottom: 10, display:'flex', alignItems:'center', gap: 8 }}>
            <MessageCircleQuestion size={16} />
            L'admin t'a demandé des précisions. Réponds ci-dessous :
          </div>
          <textarea
            rows={3}
            value={reponseClarif}
            onChange={(e) => setReponseClarif(e.target.value)}
            style={{ ...inputBase, resize: 'vertical' }}
          />
          <button onClick={repondreClarif} style={{ ...boutonPrincipal, marginTop: 10 }}>
            <Send size={14} /> Envoyer ma réponse
          </button>
        </div>
      )}

      {/* Conversation (messages) */}
      {ticket.messages?.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: tokens.inkSoft, textTransform:'uppercase', letterSpacing: 0.5 }}>
            Conversation
          </h3>
          <div style={{ display:'flex', flexDirection:'column', gap: 10 }}>
            {ticket.messages.map(m => (
              <MessageItem key={m.id} message={m} estAdmin={estAdmin} />
            ))}
          </div>
        </div>
      )}

      {/* Actions admin */}
      {estAdmin && (
        <div style={{ ...cadre, marginTop: 18, background: tokens.accentBg, borderColor: tokens.accentSoft }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 700, color: tokens.accent, textTransform:'uppercase', letterSpacing: 0.5 }}>
            Actions admin
          </h3>

          {/* Demander clarif */}
          {['nouveau', 'clarification_ia', 'pris_en_compte', 'en_cours'].includes(ticket.statut) && (
            <ZoneAction titre="Demander des précisions au déclarant">
              <textarea
                rows={2}
                placeholder="Ex : Sur quelle page exactement ? Quel message d'erreur ?"
                value={questionClarif}
                onChange={(e) => setQuestionClarif(e.target.value)}
                style={{ ...inputBase, resize:'vertical' }}
              />
              <button
                disabled={actionEnCours !== null || questionClarif.trim().length < 5}
                onClick={() => executer('demander-clarif', { message: questionClarif.trim() })}
                style={{ ...boutonSecondaire, marginTop: 8 }}
              >
                <MessageCircleQuestion size={14} /> Demander
              </button>
            </ZoneAction>
          )}

          {/* Accepter */}
          {['nouveau', 'clarification_ia', 'clarification_admin'].includes(ticket.statut) && (
            <ZoneAction titre="Accepter le ticket">
              <button
                disabled={actionEnCours !== null}
                onClick={() => executer('accepter', {})}
                style={boutonPrincipal}
              >
                <CheckCircle size={14} /> Accepter (pris en compte)
              </button>
            </ZoneAction>
          )}

          {/* Refuser */}
          {!['resolu', 'refuse', 'doublon'].includes(ticket.statut) && (
            <ZoneAction titre="Refuser le ticket">
              <input
                type="text"
                value={motifRefus}
                onChange={(e) => setMotifRefus(e.target.value)}
                placeholder="Motif du refus (visible par l'utilisateur)"
                style={inputBase}
              />
              <button
                disabled={actionEnCours !== null || motifRefus.trim().length < 5}
                onClick={() => executer('refuser', { motif: motifRefus.trim() })}
                style={{ ...boutonDanger, marginTop: 8 }}
              >
                <XCircle size={14} /> Refuser
              </button>
            </ZoneAction>
          )}

          {/* Doublon */}
          {!['resolu', 'refuse', 'doublon'].includes(ticket.statut) && (
            <ZoneAction titre="Marquer comme doublon">
              <input
                type="text"
                value={doublonToken}
                onChange={(e) => setDoublon(e.target.value.toUpperCase())}
                placeholder="Token du ticket original (ex: ABC23XYZ4567)"
                style={{ ...inputBase, fontFamily: 'monospace' }}
                maxLength={12}
              />
              <button
                disabled={actionEnCours !== null || doublonToken.length !== 12}
                onClick={() => executer('doublon', { doublon_de_token: doublonToken })}
                style={{ ...boutonSecondaire, marginTop: 8 }}
              >
                <Copy size={14} /> Marquer doublon
              </button>
            </ZoneAction>
          )}

          {/* Changements de statut linéaires */}
          {ticket.statut === 'pris_en_compte' && (
            <ZoneAction titre="Mettre en cours">
              <button
                onClick={() => executer('statut', { statut: 'en_cours' })}
                style={boutonPrincipal}
              >Passer en cours</button>
            </ZoneAction>
          )}
          {ticket.statut === 'en_cours' && (
            <ZoneAction titre="Marquer comme résolu">
              <button
                onClick={() => executer('statut', { statut: 'resolu' })}
                style={boutonPrincipal}
              >
                <CheckCircle size={14} /> Résolu
              </button>
            </ZoneAction>
          )}

          {/* Ajouter message */}
          <ZoneAction titre="Ajouter un message" derniere>
            <textarea
              rows={2}
              value={contenuMessage}
              onChange={(e) => setContenuMessage(e.target.value)}
              placeholder="Commentaire visible par l'utilisateur, ou note interne"
              style={{ ...inputBase, resize:'vertical' }}
            />
            <div style={{ marginTop: 8, display:'flex', alignItems:'center', gap: 12 }}>
              <label style={{ display:'flex', alignItems:'center', gap: 6, fontSize: 12, color: tokens.inkSoft, cursor:'pointer' }}>
                <input
                  type="checkbox"
                  checked={messageInterne}
                  onChange={(e) => setInterne(e.target.checked)}
                />
                {messageInterne ? <EyeOff size={12} /> : <Eye size={12} />}
                Note interne (invisible pour l'utilisateur)
              </label>
              <button
                disabled={contenuMessage.trim().length < 2}
                onClick={() => executer('message', {
                  contenu: contenuMessage.trim(),
                  visible_par_user: !messageInterne,
                })}
                style={{ ...boutonSecondaire, marginLeft: 'auto' }}
              >
                <Send size={14} /> Envoyer
              </button>
            </div>
          </ZoneAction>
        </div>
      )}

      {erreur && <BoiteErreur message={erreur} />}
    </div>
  );
}

function Etat({ children, onRetour }) {
  return (
    <div>
      {onRetour && (
        <button onClick={onRetour} style={{
          display:'inline-flex', alignItems:'center', gap: 4,
          background:'transparent', border:'none', cursor:'pointer',
          color: tokens.inkSoft, fontSize: 13, marginBottom: 16, padding: 0,
        }}><ArrowLeft size={14} /> Retour</button>
      )}
      <div style={{ color: tokens.inkSoft, fontSize: 14 }}>{children}</div>
    </div>
  );
}

function Section({ titre, children, derniere = false }) {
  return (
    <div style={{ marginBottom: derniere ? 0 : 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: tokens.inkSoft, textTransform:'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
        {titre}
      </div>
      <div style={{ fontSize: 14, color: tokens.ink, lineHeight: 1.5, whiteSpace:'pre-wrap' }}>
        {children}
      </div>
    </div>
  );
}

function ZoneAction({ titre, children, derniere = false }) {
  return (
    <div style={{
      paddingBottom: derniere ? 0 : 14,
      marginBottom: derniere ? 0 : 14,
      borderBottom: derniere ? 'none' : `1px solid ${tokens.accentSoft}40`,
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: tokens.accent, marginBottom: 8 }}>
        {titre}
      </div>
      {children}
    </div>
  );
}

function MessageItem({ message, estAdmin }) {
  const meta = (() => {
    switch (message.auteur_type) {
      case 'user':  return { Icone: User,   label: 'Utilisateur', bg: tokens.accentBg, fg: tokens.accent };
      case 'admin': return { Icone: Shield, label: 'Support',     bg: tokens.successBg, fg: tokens.success };
      case 'ia':    return { Icone: Bot,    label: 'IA',          bg: tokens.surfaceAlt, fg: tokens.inkSoft };
      default:      return { Icone: User,   label: '?',           bg: tokens.surfaceAlt, fg: tokens.inkSoft };
    }
  })();

  const opaque = message.auteur_type === 'ia' ? 0.85 : 1;

  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap: 10, opacity: opaque }}>
      <div style={{
        flexShrink: 0, width: 30, height: 30,
        background: meta.bg, color: meta.fg, borderRadius: '50%',
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>
        <meta.Icone size={15} />
      </div>
      <div style={{
        flex: 1, background: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: 8, padding: '8px 12px',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap: 8, marginBottom: 4 }}>
          <strong style={{ fontSize: 12, color: tokens.ink }}>{meta.label}</strong>
          {!message.visible_par_user && estAdmin && (
            <span style={{
              fontSize: 9, color: tokens.warning, background: tokens.warningBg,
              padding: '1px 6px', borderRadius: 3,
              textTransform:'uppercase', letterSpacing: 0.3,
            }}>Interne</span>
          )}
          <span style={{ marginLeft:'auto', fontSize: 10, color: tokens.inkMuted }}>
            {formaterDateLongue(message.fait_le)}
          </span>
        </div>
        <div style={{ fontSize: 13, color: tokens.ink, whiteSpace:'pre-wrap', lineHeight: 1.5 }}>
          {message.contenu}
        </div>
      </div>
    </div>
  );
}

const cadre = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 10,
  padding: 18,
};
